import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, ArrowRight, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const { tf } = useLang();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (password.length < 8) { setError(tf("8 caractères minimum")); return; }
    if (password !== confirm) { setError(tf("Les mots de passe ne correspondent pas")); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      await checkAuth();
      toast.success(tf("Mot de passe réinitialisé"));
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || tf("Erreur");
      setError(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#0A0C12] flex items-center justify-center px-5 py-10" data-testid="reset-page">
      <div className="w-full max-w-sm">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-xs text-[#93A5C0] hover:text-white transition-colors mb-6">
          <ArrowLeft className="h-3.5 w-3.5" />
          {tf("Retour")}
        </Link>

        <div className="flex items-center gap-2 mb-6">
          <img src="/athlynx-logo-white.svg" alt="Athlynx" className="h-7 w-7" />
          <span className="text-base font-semibold tracking-tight text-white">Athlynx</span>
        </div>

        <header className="mb-7 space-y-2">
          <div className="h-10 w-10 rounded-xl bg-info/10 border border-info/25 flex items-center justify-center">
            <KeyRound className="h-4 w-4 text-info" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#F5F7FA]">
            {tf("Nouveau mot de passe")}
          </h1>
          <p className="text-sm text-[#9AA9BF]">
            {tf("Choisissez un nouveau mot de passe pour votre compte.")}
          </p>
        </header>

        {!token ? (
          <div className="rounded-2xl border border-red-500/25 bg-red-500/[0.06] p-4 text-sm text-red-400" data-testid="reset-no-token">
            {tf("Lien invalide. Demandez un nouveau lien depuis la page « Mot de passe oublié ».")}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-form">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#C4D0E2]">{tf("Nouveau mot de passe")}</Label>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 pr-10 bg-[#111B28] border-[#354458] text-[#F8FAFC] placeholder:text-[#94A3B8] focus-visible:border-[#5B739B]"
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center text-[#7C8CA5] hover:text-white"
                >
                  {showPwd ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#C4D0E2]">{tf("Confirmer")}</Label>
              <Input
                type={showPwd ? "text" : "password"}
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="h-11 bg-[#111B28] border-[#354458] text-[#F8FAFC] placeholder:text-[#94A3B8] focus-visible:border-[#5B739B]"
                data-testid="reset-confirm-input"
              />
            </div>

            {error && (
              <div className="text-[12px] text-red-400" data-testid="reset-error">{error}</div>
            )}

            <Button
              type="submit"
              disabled={busy}
              className="w-full h-11 gap-2 active:scale-[0.98] transition-all duration-150 bg-[#2458D6] text-white hover:bg-[#1E4BBE] disabled:opacity-60"
              data-testid="reset-submit"
            >
              {busy ? tf("Mise à jour…") : (<>{tf("Réinitialiser")} <ArrowRight className="h-4 w-4" /></>)}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
